import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { 
  Baby,
  Ruler,
  Heart,
  Wind,
  Activity,
  Gauge,
  Syringe,
  Eye,
  Stethoscope,
  Info
} from 'lucide-react';

export default function ParametresEnfantPage() {
  const [selectedAge, setSelectedAge] = useState(null);

  const childrenData = [
    {
      id: 'nn',
      label: 'Nouveau-né',
      age: 'NN',
      poids: 3.5,
      taille: 50,
      surfaceCorporelle: 0.2,
      fr: '40 - 60',
      fc: '90 - 180',
      pas: '50 - 80',
      pam: '35 - 45',
      materiel: {
        sondeAspiration: '6 - 8',
        lameLaryngoscope: '0 - 1',
        sondeTrachéale: '3.5',
        longueurOrale: '9.5',
        sondeGastrique: '5 - 8'
      },
      color: 'bg-pink-500/10 border-pink-500/30',
      badgeColor: 'bg-pink-500'
    },
    {
      id: '3m',
      label: '3 mois',
      age: '3 mois',
      poids: 5.5,
      taille: 60,
      surfaceCorporelle: 0.3,
      fr: '30 - 40',
      fc: '80 - 180',
      pas: '65 - 95',
      pam: '40 - 50',
      materiel: {
        sondeAspiration: '8',
        lameLaryngoscope: '0 - 1',
        sondeTrachéale: '3.5',
        longueurOrale: '10.5',
        sondeGastrique: '8'
      },
      color: 'bg-purple-500/10 border-purple-500/30',
      badgeColor: 'bg-purple-500'
    },
    {
      id: '6m',
      label: '6 mois',
      age: '6 mois',
      poids: 7,
      taille: 70,
      surfaceCorporelle: 0.35,
      fr: '30 - 40',
      fc: '80 - 170',
      pas: '65 - 100',
      pam: '40 - 55',
      materiel: {
        sondeAspiration: '8',
        lameLaryngoscope: '1',
        sondeTrachéale: '4.0',
        longueurOrale: '12',
        sondeGastrique: '8'
      },
      color: 'bg-blue-500/10 border-blue-500/30',
      badgeColor: 'bg-blue-500'
    },
    {
      id: '1y',
      label: '1 an',
      age: '1 an',
      poids: 10,
      taille: 75,
      surfaceCorporelle: 0.45,
      fr: '25 - 35',
      fc: '70 - 160',
      pas: '72 - 102',
      pam: '40 - 55',
      materiel: {
        sondeAspiration: '10',
        lameLaryngoscope: '1 - 2',
        sondeTrachéale: '4.0',
        longueurOrale: '12',
        sondeGastrique: '10 - 12'
      },
      color: 'bg-cyan-500/10 border-cyan-500/30',
      badgeColor: 'bg-cyan-500'
    },
    {
      id: '2y',
      label: '2 ans',
      age: '2 ans',
      poids: 12,
      taille: 90,
      surfaceCorporelle: 0.5,
      fr: '25 - 30',
      fc: '65 - 150',
      pas: '74 - 104',
      pam: '43 - 58',
      materiel: {
        sondeAspiration: '10',
        lameLaryngoscope: '1 - 2',
        sondeTrachéale: '4.5',
        longueurOrale: '13',
        sondeGastrique: '10 - 14'
      },
      color: 'bg-green-500/10 border-green-500/30',
      badgeColor: 'bg-green-500'
    },
    {
      id: '3y',
      label: '3 ans',
      age: '3 ans',
      poids: 15,
      taille: 95,
      surfaceCorporelle: 0.6,
      fr: '25 - 30',
      fc: '65 - 140',
      pas: '76 - 105',
      pam: '45 - 59',
      materiel: {
        sondeAspiration: '10 - 12',
        lameLaryngoscope: '2',
        sondeTrachéale: '5.0',
        longueurOrale: '13.5',
        sondeGastrique: '10 - 14'
      },
      color: 'bg-yellow-500/10 border-yellow-500/30',
      badgeColor: 'bg-yellow-500'
    },
    {
      id: '5y',
      label: '5 ans',
      age: '5 ans',
      poids: 20,
      taille: 110,
      surfaceCorporelle: 0.8,
      fr: '20 - 25',
      fc: '60 - 135',
      pas: '80 - 106',
      pam: '47 - 62',
      materiel: {
        sondeAspiration: '12',
        lameLaryngoscope: '2',
        sondeTrachéale: '5.5',
        longueurOrale: '14',
        sondeGastrique: '10 - 14'
      },
      color: 'bg-amber-500/10 border-amber-500/30',
      badgeColor: 'bg-amber-500'
    },
    {
      id: '8y',
      label: '8 ans',
      age: '8 ans',
      poids: 25,
      taille: 125,
      surfaceCorporelle: 0.95,
      fr: '15 - 25',
      fc: '60 - 125',
      pas: '86 - 110',
      pam: '52 - 67',
      materiel: {
        sondeAspiration: '12 - 14',
        lameLaryngoscope: '2',
        sondeTrachéale: '6.0',
        longueurOrale: '15',
        sondeGastrique: '12 - 14'
      },
      color: 'bg-orange-500/10 border-orange-500/30',
      badgeColor: 'bg-orange-500'
    },
    {
      id: '10y',
      label: '10 ans',
      age: '10 ans',
      poids: 35,
      taille: 140,
      surfaceCorporelle: 1.2,
      fr: '15 - 25',
      fc: '60 - 120',
      pas: '90 - 115',
      pam: '65 - 80',
      materiel: {
        sondeAspiration: '14',
        lameLaryngoscope: '2 - 3',
        sondeTrachéale: '6.5',
        longueurOrale: '17',
        sondeGastrique: '14 - 16'
      },
      color: 'bg-red-500/10 border-red-500/30',
      badgeColor: 'bg-red-500'
    },
    {
      id: '14y',
      label: '14 ans',
      age: '14 ans',
      poids: 50,
      taille: 160,
      surfaceCorporelle: 1.5,
      fr: '12 - 20',
      fc: '60 - 115',
      pas: '110 - 125',
      pam: '65 - 80',
      materiel: {
        sondeAspiration: '16',
        lameLaryngoscope: '3',
        sondeTrachéale: '7.5',
        longueurOrale: '21',
        sondeGastrique: '16'
      },
      color: 'bg-indigo-500/10 border-indigo-500/30',
      badgeColor: 'bg-indigo-500'
    }
  ];

  const selectedChild = childrenData.find(c => c.id === selectedAge);

  return (
    <div className="min-h-screen bg-[hsl(var(--bg))] text-[hsl(var(--text))] p-4 md:p-6" data-testid="parametres-enfant-page">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center space-y-3">
          <div className="flex items-center justify-center gap-3">
            <Baby className="w-10 h-10 text-cyan-400" />
            <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-cyan-400 to-cyan-300 bg-clip-text text-transparent">
              Paramètres & Matériel Pédiatrique
            </h1>
          </div>
          <p className="text-[hsl(var(--text-dim))] text-sm md:text-base max-w-3xl mx-auto">
            Valeurs normales et matériel adapté à l'âge de l'enfant (du nouveau-né à 14 ans)
          </p>
        </div>

        {/* Info */}
        <Alert className="bg-cyan-500/10 border-cyan-500/30">
          <Info className="w-4 h-4 text-cyan-400" />
          <AlertDescription className="text-[hsl(var(--text))]">
            <p className="font-semibold mb-1">Utilisation :</p>
            <p className="text-sm text-[hsl(var(--text-dim))]">
              Sélectionnez une tranche d'âge pour afficher les paramètres normaux (constantes vitales, mesures anthropométriques) et le matériel adapté (sondes, lames, etc.)
            </p>
          </AlertDescription>
        </Alert>

        {/* Sélection de l'âge */}
        <Card className="bg-[hsl(var(--bg-elev))] border-[hsl(var(--border))]">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-cyan-300">
              <Baby className="w-5 h-5" />
              Sélectionnez l'Âge de l'Enfant
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {childrenData.map((child) => {
                const isSelected = selectedAge === child.id;
                
                return (
                  <button
                    key={child.id}
                    onClick={() => setSelectedAge(child.id)}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      isSelected
                        ? `${child.color} border-current shadow-lg scale-105`
                        : 'bg-[hsl(var(--bg))] border-[hsl(var(--border))] hover:border-cyan-500/30'
                    }`}
                    data-testid={`age-${child.id}`}
                  >
                    <Badge className={`${child.badgeColor} text-white mb-2 w-full justify-center`}>
                      {child.label}
                    </Badge>
                    <p className="text-xs text-[hsl(var(--text-dim))]">{child.poids} kg</p>
                    <p className="text-xs text-[hsl(var(--text-dim))]">{child.taille} cm</p>
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Détails pour l'âge sélectionné */}
        {selectedChild && (
          <>
            {/* Paramètres Normaux */}
            <Card className={`${selectedChild.color} border-2`} data-testid="parametres-normaux">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-[hsl(var(--text))]">
                  <Activity className="w-5 h-5 text-cyan-400" />
                  Paramètres Normaux - {selectedChild.label}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {/* Âge */}
                  <div className="p-4 rounded-lg bg-[hsl(var(--bg))] border border-[hsl(var(--border))]">
                    <div className="flex items-center gap-2 mb-2">
                      <Baby className="w-4 h-4 text-cyan-400" />
                      <span className="text-xs font-semibold text-[hsl(var(--text-dim))]">Âge</span>
                    </div>
                    <p className="text-lg font-bold text-[hsl(var(--text))]">{selectedChild.age}</p>
                  </div>

                  {/* Poids */}
                  <div className="p-4 rounded-lg bg-[hsl(var(--bg))] border border-[hsl(var(--border))]">
                    <div className="flex items-center gap-2 mb-2">
                      <Gauge className="w-4 h-4 text-cyan-400" />
                      <span className="text-xs font-semibold text-[hsl(var(--text-dim))]">Poids</span>
                    </div>
                    <p className="text-lg font-bold text-[hsl(var(--text))]">{selectedChild.poids} kg</p>
                  </div>

                  {/* Taille */}
                  <div className="p-4 rounded-lg bg-[hsl(var(--bg))] border border-[hsl(var(--border))]">
                    <div className="flex items-center gap-2 mb-2">
                      <Ruler className="w-4 h-4 text-cyan-400" />
                      <span className="text-xs font-semibold text-[hsl(var(--text-dim))]">Taille</span>
                    </div>
                    <p className="text-lg font-bold text-[hsl(var(--text))]">{selectedChild.taille} cm</p>
                  </div>

                  {/* Surface corporelle */}
                  <div className="p-4 rounded-lg bg-[hsl(var(--bg))] border border-[hsl(var(--border))]">
                    <div className="flex items-center gap-2 mb-2">
                      <Activity className="w-4 h-4 text-cyan-400" />
                      <span className="text-xs font-semibold text-[hsl(var(--text-dim))]">Surface Corporelle</span>
                    </div>
                    <p className="text-lg font-bold text-[hsl(var(--text))]">{selectedChild.surfaceCorporelle} m²</p>
                  </div>

                  {/* FR */}
                  <div className="p-4 rounded-lg bg-[hsl(var(--bg))] border border-[hsl(var(--border))]">
                    <div className="flex items-center gap-2 mb-2">
                      <Wind className="w-4 h-4 text-cyan-400" />
                      <span className="text-xs font-semibold text-[hsl(var(--text-dim))]">Fréquence Respiratoire</span>
                    </div>
                    <p className="text-lg font-bold text-[hsl(var(--text))]">{selectedChild.fr} /min</p>
                  </div>

                  {/* FC */}
                  <div className="p-4 rounded-lg bg-[hsl(var(--bg))] border border-[hsl(var(--border))]">
                    <div className="flex items-center gap-2 mb-2">
                      <Heart className="w-4 h-4 text-cyan-400" />
                      <span className="text-xs font-semibold text-[hsl(var(--text-dim))]">Fréquence Cardiaque</span>
                    </div>
                    <p className="text-lg font-bold text-[hsl(var(--text))]">{selectedChild.fc} /min</p>
                  </div>

                  {/* PAS */}
                  <div className="p-4 rounded-lg bg-[hsl(var(--bg))] border border-[hsl(var(--border))]">
                    <div className="flex items-center gap-2 mb-2">
                      <Activity className="w-4 h-4 text-cyan-400" />
                      <span className="text-xs font-semibold text-[hsl(var(--text-dim))]">PAS (inf-normale)</span>
                    </div>
                    <p className="text-lg font-bold text-[hsl(var(--text))]">{selectedChild.pas} mmHg</p>
                  </div>

                  {/* PAM */}
                  <div className="p-4 rounded-lg bg-[hsl(var(--bg))] border border-[hsl(var(--border))]">
                    <div className="flex items-center gap-2 mb-2">
                      <Gauge className="w-4 h-4 text-cyan-400" />
                      <span className="text-xs font-semibold text-[hsl(var(--text-dim))]">PAM (limite inf-normale)</span>
                    </div>
                    <p className="text-lg font-bold text-[hsl(var(--text))]">{selectedChild.pam} mmHg</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Matériel */}
            <Card className="bg-[hsl(var(--bg-elev))] border-[hsl(var(--border))]" data-testid="materiel-adapte">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-cyan-300">
                  <Syringe className="w-5 h-5" />
                  Matériel Adapté - {selectedChild.label}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {/* Sonde d'aspiration */}
                  <div className="p-4 rounded-lg bg-cyan-500/10 border border-cyan-500/30">
                    <div className="flex items-center gap-2 mb-2">
                      <Syringe className="w-4 h-4 text-cyan-400" />
                      <span className="text-xs font-semibold text-cyan-300">Sonde d'Aspiration</span>
                    </div>
                    <p className="text-lg font-bold text-[hsl(var(--text))]">N° {selectedChild.materiel.sondeAspiration} Ch</p>
                  </div>

                  {/* Lame laryngoscope */}
                  <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30">
                    <div className="flex items-center gap-2 mb-2">
                      <Eye className="w-4 h-4 text-amber-400" />
                      <span className="text-xs font-semibold text-amber-300">Lame Laryngoscope</span>
                    </div>
                    <p className="text-lg font-bold text-[hsl(var(--text))]">N° {selectedChild.materiel.lameLaryngoscope}</p>
                  </div>

                  {/* Sonde trachéale */}
                  <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/30">
                    <div className="flex items-center gap-2 mb-2">
                      <Wind className="w-4 h-4 text-green-400" />
                      <span className="text-xs font-semibold text-green-300">Sonde Trachéale</span>
                    </div>
                    <p className="text-lg font-bold text-[hsl(var(--text))]">N° {selectedChild.materiel.sondeTrachéale}</p>
                  </div>

                  {/* Longueur orale */}
                  <div className="p-4 rounded-lg bg-purple-500/10 border border-purple-500/30">
                    <div className="flex items-center gap-2 mb-2">
                      <Ruler className="w-4 h-4 text-purple-400" />
                      <span className="text-xs font-semibold text-purple-300">Longueur Orale</span>
                    </div>
                    <p className="text-lg font-bold text-[hsl(var(--text))]">{selectedChild.materiel.longueurOrale} cm</p>
                  </div>

                  {/* Sonde gastrique */}
                  <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/30">
                    <div className="flex items-center gap-2 mb-2">
                      <Stethoscope className="w-4 h-4 text-blue-400" />
                      <span className="text-xs font-semibold text-blue-300">Sonde Gastrique</span>
                    </div>
                    <p className="text-lg font-bold text-[hsl(var(--text))]">N° {selectedChild.materiel.sondeGastrique} Ch</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* Tableau récapitulatif */}
        <Card className="bg-[hsl(var(--bg-elev))] border-[hsl(var(--border))]">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-cyan-300">
              <Activity className="w-5 h-5" />
              Tableau Récapitulatif Complet
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-[hsl(var(--border))]">
                    <th className="text-left p-2 font-semibold text-[hsl(var(--text))]">Âge</th>
                    <th className="text-left p-2 font-semibold text-[hsl(var(--text))]">Poids (kg)</th>
                    <th className="text-left p-2 font-semibold text-[hsl(var(--text))]">Taille (cm)</th>
                    <th className="text-left p-2 font-semibold text-[hsl(var(--text))]">SC (m²)</th>
                    <th className="text-left p-2 font-semibold text-[hsl(var(--text))]">FR (/min)</th>
                    <th className="text-left p-2 font-semibold text-[hsl(var(--text))]">FC (/min)</th>
                    <th className="text-left p-2 font-semibold text-[hsl(var(--text))]">PAS (mmHg)</th>
                    <th className="text-left p-2 font-semibold text-[hsl(var(--text))]">PAM (mmHg)</th>
                  </tr>
                </thead>
                <tbody>
                  {childrenData.map((child) => (
                    <tr 
                      key={child.id}
                      className={`border-b border-[hsl(var(--border))] hover:bg-[hsl(var(--bg))] cursor-pointer transition-colors ${
                        selectedAge === child.id ? child.color : ''
                      }`}
                      onClick={() => setSelectedAge(child.id)}
                    >
                      <td className="p-2">
                        <Badge className={`${child.badgeColor} text-white text-xs`}>
                          {child.label}
                        </Badge>
                      </td>
                      <td className="p-2 text-[hsl(var(--text-dim))]">{child.poids}</td>
                      <td className="p-2 text-[hsl(var(--text-dim))]">{child.taille}</td>
                      <td className="p-2 text-[hsl(var(--text-dim))]">{child.surfaceCorporelle}</td>
                      <td className="p-2 text-[hsl(var(--text-dim))]">{child.fr}</td>
                      <td className="p-2 text-[hsl(var(--text-dim))]">{child.fc}</td>
                      <td className="p-2 text-[hsl(var(--text-dim))]">{child.pas}</td>
                      <td className="p-2 text-[hsl(var(--text-dim))]">{child.pam}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
